import {
  CheckCommodityAvailabilityPayload,
  CommodityForwardCurvesDataPayload,
  CommodityForwardCurvesFormData,
  CommodityVolatilityCurvesDataPayload,
  CorrelatedMarketDataPayload,
  DiscountFactorsPayload,
  FxAnalysisPayload,
  FxForwardCurvesPayload,
  HistoricalIrDifferencialByCurrencyPayload,
  IrDifferentialCurvePayload,
  ValidateFxCurrencyParams,
  ValidateIrIndexParams,
} from '@/pages/MarketCurvesCalculation/store/types';
import type {
  SignalChatRequest,
  SignalHistoryMessage,
} from '@/pages/MarketCurvesCalculation/components/MarketDataAnalyzer/types';
import {
  DownloadHistoricalForwardCurveReportPayloadTypes,
  DownloadInterestCostCalcReportPayloadTypes,
  DownloadIrAnalysisPayloadTypes,
  DownloadIrDifferencialCurrencyReportPaylaodTypes,
  DownloadIrForwardCurveReportPayloadTypes,
  DownloadSecuritiesDataReportPayloadTypes,
  FxOptionVolatilityBackendRequest,
  IrCapletVolatilityBackendRequest,
} from '@/store/marketData/types';
import { AxiosInstance } from 'axios';

export default class MarketDataAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async fetchIrIndexList() {
    return this.client.get('/v2/market-data/index-list/');
  }

  async fetchCurrencyPair() {
    return this.client.get('/v2/market-data/get-currency-pair-form/');
  }

  async fetchSecurities() {
    return this.client.get('/v2/market-data/get-all-securities/');
  }

  async getCurrencyPairForm() {
    return this.client.get('/v2/market-data/get-currency-pair-form');
  }

  async getCommoditiesIndices() {
    return this.client.get('/v2/market-data/commodity-indices/');
  }

  async validateIrIndex(data: ValidateIrIndexParams) {
    return this.client.post('/v2/market-data/validate-ir-index/', data);
  }

  async downloadIrForwardCurveReport(
    data: DownloadIrForwardCurveReportPayloadTypes,
  ) {
    return this.client.post(
      '/v2/market-data/download-ir-forward-curve-report/',
      data,
    );
  }

  async downloadSecuritiesDataReport(
    data: DownloadSecuritiesDataReportPayloadTypes,
  ) {
    return this.client.post(
      '/v2/reporting/download-security-data-report/',
      data,
    );
  }

  async donwloadIrAnalysisReport(data: DownloadIrAnalysisPayloadTypes) {
    return this.client.post(
      '/v2/market-data/download-ir-analysis-report/',
      data,
    );
  }

  async downloadInterestCostCalcReport(
    data: DownloadInterestCostCalcReportPayloadTypes,
  ) {
    return this.client.post('/v2/reporting/download-rfr-report/', data);
  }

  async downloadHistoricalForwardCurveReport(
    data: DownloadHistoricalForwardCurveReportPayloadTypes,
  ) {
    return this.client.post(
      '/v2/market-data/download-historical-fwd-curve-report/',
      data,
    );
  }

  async downloadIrDifferencialCurrencyReport(
    data: DownloadIrDifferencialCurrencyReportPaylaodTypes,
  ) {
    return this.client.post('/v2/market-data/return-ir-differential/', data);
  }

  async downloadHairyChartReport(data: any) {
    return this.client.post('/v2/reporting/download-hairy-chart-report/', data);
  }

  async getHairyChartSelections(data: {
    start_date: string;
    end_date: string;
  }) {
    return this.client.post('/v2/reporting/hairy-chart-selections/', data);
  }

  async getHairyChartData(data: {
    start_date: string;
    end_date: string;
    frequency: number;
    tenor: number;
    ir_index?: number;
    left_curr_id?: number;
    right_curr_id?: number;
  }) {
    return this.client.post('/v2/reporting/hairy-chart-data/', data);
  }

  async emailIrForwardCurveReport(data: any) {
    return this.client.post(
      '/v2/communication/market-data/email-ir-forward-curves-widget/',
      data,
    );
  }

  async reportFxForwardCurves(payload: FxForwardCurvesPayload) {
    const url = '/v2/market-data/download-fx-forward-curve-report/';

    return this.client.post(url, payload);
  }

  async validateFxCurrency(params: ValidateFxCurrencyParams) {
    const url = '/v2/market-data/validate-fx-currency/';
    return this.client.post(url, params);
  }

  async reportIRDifferential(
    calculationDate: string,
    forwardDate: string,
    baseCurrency: number,
    localCurrency: number,
  ) {
    const payload = {
      calculation_date: calculationDate,
      forward_date: forwardDate,
      base_curr_id: baseCurrency,
      local_curr_id: localCurrency,
    };

    const url = '/market-data/return-ir-differential/';

    return this.client.post(url, payload);
  }

  async reportHistIRDifferential(
    start_date: string,
    end_date: string,
    forwardIncrements: { id: string; label: string }[],
    currency_pairs: { left_currency: number; right_currency: number }[],
  ) {
    const payload = {
      start_date,
      end_date,
      fwd_date_increments: forwardIncrements.map((item) => item.id),
      currency_pairs: currency_pairs.map((pair) => ({
        base_curr_id: pair.left_currency,
        local_curr_id: pair.right_currency,
      })),
    };

    const url = '/reporting/download-historical-ir-differential-report/';

    return this.client.post(url, payload);
  }

  async returnCommoditiesIndices() {
    const url = '/market-data/return-commodities-indices/';
    return this.client.get(url);
  }

  async checkCommodityAvailability(data: CheckCommodityAvailabilityPayload) {
    const url =
      '/v2/market-data/check-data-availability-commodities-forward-curves/';
    return this.client.post(url, data);
  }

  async downloadCommodityForwardCurveReport(
    payload: CommodityForwardCurvesFormData,
  ) {
    const url = '/v2/market-data/download-commodities-forward-curves-report/';
    return this.client.post(url, payload);
  }

  async getTimeFrequencyIR() {
    const url = '/reporting/get-pay-frequency-details/';
    return this.client.post(url);
  }

  async downloadFXAnalysisReport(payload: {
    leftCurrencyID: number;
    rightCurrencyID: number;
    runDate: string;
    numDaysToForecast: number;
  }) {
    const url = '/market-data/run-fx-analysis/';
    return await this.client.post(url, payload);
  }

  async getBusinessDays() {
    const url = '/market-data/get-business-days/';
    return await this.client.get(url);
  }

  async downloadRfrReport(payload: {
    notional: string;
    runDate: string;
    startDate: string;
    endDate: string;
    index: string;
    selectedBusinessDays: number[];
    lagDays: number;
    observationShift: string;
  }) {
    const url = '/market-data/download-rfr-report/';
    return await this.client.post(url, payload);
  }

  async downloadFxAnalysis(payload: FxAnalysisPayload) {
    const url = '/v2/market-data/fx-analysis/';
    return await this.client.post(url, payload);
  }

  async downloadDiscountFactors(payload: DiscountFactorsPayload) {
    const url = '/v2/market-data/download-discount-factor-report/';
    return await this.client.post(url, payload);
  }

  async downloadCorrelatedMarketDataSimulations(payload: {
    runDate: string;
    pairs: { index: string; currency: string }[];
    num_of_sims: number;
    tenor: number;
    correlation_history: number;
  }) {
    const url = '/market-data/download-monte-carlo-sims/';
    return await this.client.post(url, payload);
  }

  async fetchAllSecurities() {
    const url = '/market-data/get-all-securities/';
    return this.client.get(url);
  }

  async downloadSecurityDataWidgetReport(payload: {
    start_date: string;
    end_date: string;
    securities: string[];
  }) {
    const url = '/reporting/download-security-data-widget-report/';
    return this.client.post(url, payload);
  }

  async downloadMarketUpdateReport(payload: { run_date: string }) {
    const url = '/v2/market-data/download-market-update-report/';
    return this.client.post(url, payload);
  }

  async downloadIrCapletVolatility(payload: IrCapletVolatilityBackendRequest) {
    const [item] = payload;
    const body = {
      run_date: item.date,
      index_id: item.indexId,
      years: item.expiryYears.map((y) => Number(y)),
      strikes: item.strikeList.map((s) => Number(s)),
    };
    const url = '/v2/market-data/download-ir-model-report/';
    return this.client.post(url, body);
  }

  async downloadFxOptionVolatility(payload: FxOptionVolatilityBackendRequest) {
    const url = 'v2/market-data/download-fx-option-vol/';
    return this.client.post(url, { items: payload });
  }

  async downloadCDSCurveReport(payload: { run_date: string }) {
    const url = '/v2/market-data/download-cds-curve-report/';
    return this.client.post(url, payload);
  }

  async downloadCorrelatedMarketDataReport(
    payload: CorrelatedMarketDataPayload,
  ) {
    const url = '/v2/market-data/download-monte-carlo-sims-report/';
    return this.client.post(url, payload);
  }

  async downloadHistoricalIrDiffByCurrency(
    payload: HistoricalIrDifferencialByCurrencyPayload,
  ) {
    const url = '/v2/reporting/download-historical-ir-differential-report/';
    return this.client.post(url, payload);
  }

  async getFxForwardCurvesData(payload: FxForwardCurvesPayload) {
    return this.client.post('/v2/market-data/fx-forward-curves/', payload);
  }

  async getForwardCurvesData(
    payload: DownloadIrForwardCurveReportPayloadTypes,
  ) {
    const url = '/v2/market-data/ir-forward-curves/';
    return this.client.post(url, payload);
  }

  async getHistoricalForwardCurvesData(
    payload: DownloadHistoricalForwardCurveReportPayloadTypes,
  ) {
    const url = '/v2/market-data/historical-fwd-curves/';
    return this.client.post(url, payload);
  }

  async getInterestRateAnalysisData(payload: DownloadIrAnalysisPayloadTypes) {
    const url = '/v2/market-data/ir-analysis/';
    return this.client.post(url, payload);
  }

  async getInterestCostCalculationsData(
    payload: DownloadInterestCostCalcReportPayloadTypes,
  ) {
    const url = '/v2/reporting/rfr-analysis/';
    return this.client.post(url, payload);
  }

  async getFxAnalysisData(payload: FxAnalysisPayload) {
    const url = '/v2/reporting/get-fx-analysis/';
    return await this.client.post(url, payload);
  }

  async getCommoditiesFCData(payload: CommodityForwardCurvesDataPayload) {
    const url = '/v2/market-data/commodities-forward-curves/';
    return await this.client.post(url, payload);
  }

  async getCommoditiesVolCurvesData(
    payload: CommodityVolatilityCurvesDataPayload,
  ) {
    const url = '/v2/market-data/commodities-volatility-curves/';
    return await this.client.post(url, payload);
  }

  async getMarketOverviewCurrencyRates() {
    return await this.client.get('/v2/market-data/currency-rates/');
  }

  async getMarketOverviewInterestRates() {
    return await this.client.get('/v2/market-data/interest-rates/');
  }

  async getRiskFreeRateIndices() {
    return await this.client.get('/v2/market-data/risk-free-rate-indices/');
  }

  async fetchIrDifferentialCurve(payload: IrDifferentialCurvePayload) {
    const url = '/v2/market-data/return-ir-differential-curve/';
    return this.client.post(url, payload);
  }

  // ── Signal (server-side AI assistant) ──────────────────────────────────────
  // The Anthropic key, system prompt, tool catalogue and tool loop all live on
  // the backend; the browser only sends the user's message plus the committed
  // text history. The streaming variant of /signal/chat/ is the production path
  // and does not go through axios (no streaming response body in the browser) —
  // see MarketDataAnalyzer/hooks/streamSignalChat.ts. This method is the
  // non-streaming fallback for the same endpoint.
  async signalChat(payload: SignalChatRequest) {
    return this.client.post('/v2/market-data/signal/chat/', payload);
  }

  // One tool (render_chart), no tool loop. Requires the SponsorDashboardAIChat
  // permission, which Signal's marketDataAI does not grant.
  async signalSponsorChat(payload: SignalChatRequest) {
    return this.client.post('/v2/market-data/signal/sponsor-chat/', payload);
  }

  // End-of-session distillation. Best-effort by design: a failure comes back as
  // { saved: 0 } rather than an error, so it can never break the chat.
  async signalDistill(payload: { transcript: SignalHistoryMessage[] }) {
    return this.client.post('/v2/market-data/signal/distill/', payload);
  }

  // ── Signal cross-session "skills" (per-user store) ─────────────────────────
  // Durable, non-advisory user-preference notes distilled at the end of each
  // session. Scoped server-side to (firm, user) by the cookie session; the
  // client sends the full capped list.
  //   GET  /v2/market-data/signal/skills/        -> { data: MarketSkill[] }
  //   POST /v2/market-data/signal/skills/  { skills: MarketSkill[] } -> 200
  // where MarketSkill = { id, name, description, body, createdAt, updatedAt, hits }.
  async getSignalSkills() {
    return this.client.get('/v2/market-data/signal/skills/');
  }

  async saveSignalSkills(payload: { skills: unknown[] }) {
    return this.client.post('/v2/market-data/signal/skills/', payload);
  }
}
